/**
 * Intake with photos and a signature on the phone (features "intakePhotos" and "phoneSignature",
 * owner's design of 2026-11-04).
 *
 * The workshop photographs the car at intake: five base views (front, left side, rear, right side,
 * dashboard with the odometer and the fuel level) and any number of damage close ups. The photos are
 * ordinary rows of public.photos with kind 'intake' and the view in intake_view. The customer may sign
 * the intake receipt (and later the handover) with a finger on the phone; the signature keeps a SHA-256
 * fingerprint of the receipt's content at that moment, so a later change of the receipt shows.
 *
 * Pure rules only (no browser, no database): the app, the database functions and the tests use them.
 */
import { legalProfile } from "./legal";

/** The five base views, in the order the guided capture asks for them. */
export const INTAKE_BASE_VIEWS = ["front", "left", "rear", "right", "dashboard"] as const;
export type IntakeBaseView = (typeof INTAKE_BASE_VIEWS)[number];
/** Any number of close ups of damage after the base views. */
export type IntakeView = IntakeBaseView | "damage";
export const INTAKE_VIEWS: readonly IntakeView[] = [...INTAKE_BASE_VIEWS, "damage"];

export function isIntakeView(v: unknown): v is IntakeView {
  return typeof v === "string" && (INTAKE_VIEWS as readonly string[]).includes(v);
}

export type IntakeMode = "offer" | "required";
export type IntakeSend = "ifEmail" | "never";
/** How long intake photos are kept after the work order was closed. */
export type IntakeRetention = "warrantyPlus1y" | "5y";

export interface IntakeSettings {
  mode: IntakeMode;
  /** At least this many intake photos before saving, in "required" mode. */
  min: number;
  send: IntakeSend;
  retention: IntakeRetention;
}

/** Keep equal to the defaults in settings.ts. */
export const INTAKE_PHOTO_MIN_DEFAULT = 5;
export const INTAKE_PHOTO_MIN_LIMIT = 20;

/** The workshop's intake settings from settings.general, with defaults and sane limits. */
export function intakeSettings(general: Record<string, unknown>): IntakeSettings {
  const min = Math.round(Number(general.intakePhotoMin));
  return {
    mode: general.intakePhotoMode === "required" ? "required" : "offer",
    min: Number.isFinite(min) && min >= 1 ? Math.min(min, INTAKE_PHOTO_MIN_LIMIT) : INTAKE_PHOTO_MIN_DEFAULT,
    send: general.intakePhotoSend === "never" ? "never" : "ifEmail",
    retention: general.intakePhotoRetention === "5y" ? "5y" : "warrantyPlus1y",
  };
}

/** What still blocks saving in "required" mode: null when nothing does. */
export function intakeMissing(s: Pick<IntakeSettings, "mode" | "min">, count: number): { needed: number; done: number } | null {
  if (s.mode !== "required" || count >= s.min) return null;
  return { needed: s.min, done: count };
}

/**
 * What the mechanic's intake still needs before "Átvétel kész" (and what the grey Kész and Lezárás buttons list
 * while it waits): the odometer reading, and the intake photos in "required" mode. Texts: `intakeWalk.need.<key>`.
 */
export type IntakeNeed = "odometerKm" | "photos";
export function intakeNeeds(
  o: { odometerKm: number | null | undefined },
  s: Pick<IntakeSettings, "mode" | "min">,
  photos: number,
  photosOn: boolean,
): IntakeNeed[] {
  const out: IntakeNeed[] = [];
  if (o.odometerKm === null || o.odometerKm === undefined) out.push("odometerKm");
  if (photosOn && intakeMissing(s, photos)) out.push("photos");
  return out;
}

/** The base views not photographed yet, in capture order. */
export function missingBaseViews(done: readonly (IntakeView | null | undefined)[]): IntakeBaseView[] {
  return INTAKE_BASE_VIEWS.filter((v) => !done.includes(v));
}

/**
 * The next view the guided capture asks for: the first base view that is neither done nor skipped
 * in this round, then damage close ups until the user is finished.
 */
export function nextIntakeView(done: readonly (IntakeView | null | undefined)[], skipped: readonly IntakeView[] = []): IntakeView {
  return INTAKE_BASE_VIEWS.find((v) => !done.includes(v) && !skipped.includes(v)) ?? "damage";
}

/**
 * The thumbnails of the printed receipt: base views in their order first, then the damage close
 * ups (oldest first), at most `max`; `more` says how many are only on the customer's link.
 */
export function intakeCollage<T extends { view: IntakeView | null; createdAt: string }>(photos: readonly T[], max = 8): { shown: T[]; more: number } {
  const rank = (p: T) => (p.view && p.view !== "damage" ? INTAKE_BASE_VIEWS.indexOf(p.view) : INTAKE_BASE_VIEWS.length);
  const sorted = [...photos].sort((a, b) => rank(a) - rank(b) || a.createdAt.localeCompare(b.createdAt));
  const shown = sorted.length > max ? sorted.slice(0, max - 1) : sorted;
  return { shown, more: sorted.length - shown.length };
}

/**
 * Last day the intake photos of a closed work order are kept (YYYY-MM-DD); after it the daily job
 * deletes them. "warrantyPlus1y": the statutory warranty of the workshop's country (Hungary 6 months
 * from handover) plus one year; "5y": five years after closing. Same rule as
 * public.intake_photo_keep_until in the database (a database test checks both).
 */
export function intakePhotoKeepUntil(closedAtIso: string, retention: IntakeRetention, legalCountry: string | null | undefined): string {
  const months = retention === "5y" ? 60 : (legalProfile(legalCountry ?? "HU").consumerWarranty?.months ?? 0) + 12;
  const [y, m, d] = closedAtIso.slice(0, 10).split("-").map(Number) as [number, number, number];
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return target.toISOString().slice(0, 10);
}

/**
 * What else is noted at intake (owner's idea, part of the "intakePhotos" feature): what was left in the car.
 * Stored in public.work_orders.intake_extras (null when nothing is given); the database checks the same shape
 * (public.intake_extras_valid). The fuel level was dropped by the owner (2026-11-04): it shows on the dashboard photo.
 */
/** The ticks of "Autóban hagyva", in the order they are shown and printed. */
export const LEFT_IN_CAR = ["registration", "serviceBook", "spareWheel", "lockingWheelKey"] as const;
export type LeftInCar = (typeof LEFT_IN_CAR)[number];
export const INTAKE_KEYS_MAX = 20;
export const INTAKE_OTHER_MAX = 200;

export interface IntakeExtras {
  /** Ticked items, in LEFT_IN_CAR order. */
  left: LeftInCar[];
  /** Number of keys left with the car (1 to 20), null: not given. */
  keys: number | null;
  /** Anything else left in the car, a short text. */
  other: string | null;
}

export const EMPTY_INTAKE_EXTRAS: IntakeExtras = { left: [], keys: null, other: null };

/**
 * The extras in their stored form: unknown or invalid parts dropped, ticks in order, the text trimmed and cut;
 * null when nothing is given (the column stays empty).
 */
export function cleanIntakeExtras(v: unknown): IntakeExtras | null {
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const ticked = Array.isArray(o.left) ? o.left : [];
  const left = LEFT_IN_CAR.filter((x) => ticked.includes(x));
  const keys = typeof o.keys === "number" && Number.isInteger(o.keys) && o.keys >= 1 && o.keys <= INTAKE_KEYS_MAX ? o.keys : null;
  const other = typeof o.other === "string" && o.other.trim() ? o.other.trim().slice(0, INTAKE_OTHER_MAX) : null;
  if (left.length === 0 && keys === null && !other) return null;
  return { left, keys, other };
}

/** Whether two extras say the same (null and empty are the same). */
export function sameIntakeExtras(a: unknown, b: unknown): boolean {
  return canonicalJson(cleanIntakeExtras(a)) === canonicalJson(cleanIntakeExtras(b));
}

/** The number of keys typed in: null when empty, undefined when it is not a whole number from 1 to 20. */
export function parseKeyCount(text: string): number | null | undefined {
  const s = text.trim();
  if (!s) return null;
  if (!/^\d{1,2}$/.test(s)) return undefined;
  const n = Number(s);
  return n >= 1 && n <= INTAKE_KEYS_MAX ? n : undefined;
}

export type SignatureKind = "intake" | "handover";

/**
 * The version of the signed content. Version 1 (no `v`, signatures before 2026-11-04) has no intake extras;
 * version 2 adds them to the intake receipt. A signature is always checked with the version it was made with,
 * so the older ones keep matching.
 */
export const SIGNED_CONTENT_VERSION = 2;

/** What the customer signs: the facts of the receipt at the moment of signing. */
export interface SignedContent {
  kind: SignatureKind;
  number: string;
  plate: string;
  km: number | null;
  customerName: string;
  request: string;
  expectedReadyAt: string | null;
  /** Ids of the intake photos the receipt refers to (sorted). */
  photoIds: string[];
  /** The time of signing on the phone (ISO). */
  signedAt: string;
  signerName: string;
  /** Handover only: the gross total of the closed work order in minor units. */
  gross?: number;
  /** The content version: absent for version 1, 2 from 2026-11-04 (see SIGNED_CONTENT_VERSION). */
  v?: number;
  /** Version 2 intake only: what was left in the car (null: nothing given). */
  extras?: IntakeExtras | null;
}

/** The content with fixed field order, trimmed texts and sorted photo ids. */
export function signedContent(c: SignedContent): SignedContent {
  const out: SignedContent = {
    kind: c.kind,
    number: c.number,
    plate: c.plate,
    km: c.km ?? null,
    customerName: c.customerName.trim(),
    request: c.request.trim(),
    expectedReadyAt: c.expectedReadyAt ?? null,
    photoIds: [...c.photoIds].sort(),
    signedAt: c.signedAt,
    signerName: c.signerName.trim(),
  };
  if (c.kind === "handover") out.gross = c.gross ?? 0;
  // version 2 of the intake receipt adds the extras; version 1 stays exactly as it was signed
  if (c.kind === "intake" && (c.v ?? 1) >= 2) {
    out.v = 2;
    out.extras = cleanIntakeExtras(c.extras ?? null);
  }
  return out;
}

/** Canonical JSON: keys sorted at every level, no spaces, so the same content always gives the same text. */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value ?? null);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  const entries = Object.entries(value as Record<string, unknown>).filter(([, v]) => v !== undefined).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(",")}}`;
}

/** SHA-256 as 64 lower case hex characters (Web Crypto: browsers and Node). */
export async function sha256Hex(text: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** The fingerprint of a signed receipt. */
export function contentHash(c: SignedContent): Promise<string> {
  return sha256Hex(canonicalJson(signedContent(c)));
}

/** The first characters of the fingerprint as printed: "a3f9 7c21 5b0e". */
export function shortHash(hash: string, groups = 3): string {
  return (hash.match(/.{1,4}/g) ?? []).slice(0, groups).join(" ");
}

/** The customer's objection text: trimmed, 3 to 2000 characters. */
export function cleanObjection(text: string): string | null {
  const s = text.trim();
  return s.length >= 3 && s.length <= 2000 ? s : null;
}
